import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import type { Asset } from '@shared/types'
import { useContainerWidth, useJustifiedRows } from '@shared/hooks/useJustifiedRows'

/** The Internal Library's own row height and gap (AssetGrid.tsx), so the two read as one. */
const TARGET_HEIGHT = 180
const GAP = 14

/**
 * Any list of pictures, laid out the way the Internal Library is (Huanrui,
 * 2026-10-22): rows at one height, each picture at its own proportions,
 * every row ending flush -- instead of square white boxes with the picture
 * fitted inside. The cards themselves are the caller's, drawn by `render` at
 * the size given, so each page keeps its own picking, sweeping and buttons.
 *
 * `ratio` is width over height; null while not known (it is then laid out
 * square until it is).
 */
export function JustifiedRows<T>({
  items,
  keyOf,
  ratio,
  render,
  className = '',
}: {
  items: T[]
  keyOf: (item: T) => string
  ratio: (item: T) => number | null
  render: (item: T, width: number, height: number) => ReactNode
  className?: string
}) {
  const { ref, width } = useContainerWidth<HTMLDivElement>()

  // The layout works on assets; only an id and a shape are read from them.
  const byKey = useMemo(() => new Map(items.map((item) => [keyOf(item), item])), [items, keyOf])
  const shapes = useMemo(
    () =>
      items.map((item) => {
        const r = ratio(item) || 1
        return { id: keyOf(item), width: Math.round(r * 1000), height: 1000 } as Asset
      }),
    [items, keyOf, ratio],
  )
  const rows = useJustifiedRows(shapes, width, TARGET_HEIGHT, GAP)

  return (
    <div ref={ref} className={`ww-grid${className ? ` ${className}` : ''}`}>
      {rows.map((row) => (
        <div className="ww-row" key={row[0].asset.id}>
          {row.map(({ asset, width: w, height: h }) => {
            const item = byKey.get(asset.id)
            return item === undefined ? null : render(item, w, h)
          })}
        </div>
      ))}
    </div>
  )
}

/**
 * The shapes of pictures whose size nobody gave (the odd Unsplash one): each one is
 * fetched the moment it is listed rather than when it scrolls into view, so
 * the rows settle while the top of the page is being looked at, and the
 * answers are passed on in batches rather than one re-layout per picture.
 * Width over height, by URL.
 */
export function usePictureRatios(urls: string[]): Map<string, number> {
  const [ratios, setRatios] = useState<Map<string, number>>(() => new Map())
  const known = useRef(new Map<string, number>())
  const asked = useRef(new Set<string>())
  const timer = useRef<number | null>(null)

  useEffect(() => {
    const flush = () => {
      timer.current = null
      setRatios(new Map(known.current))
    }
    for (const url of urls) {
      if (!url || asked.current.has(url)) continue
      asked.current.add(url)
      const probe = new Image()
      probe.referrerPolicy = 'no-referrer'
      probe.onload = () => {
        if (probe.naturalWidth > 0 && probe.naturalHeight > 0) {
          known.current.set(url, probe.naturalWidth / probe.naturalHeight)
          if (timer.current === null) timer.current = window.setTimeout(flush, 120)
        }
      }
      probe.src = url
    }
  }, [urls])

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    },
    [],
  )

  return ratios
}
